/* ============================================================
   Dathent — Referrals dashboard (/app/referrals)
   Real data wired to /api/referral/me + /api/referral/me/signups + /api/referral/me/payouts.
   Source: design/pages/referrals.jsx, design/Referrals.html
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';
import { referralProgramV2Enabled } from '../lib/flags.js';

const DEFAULT_GRADIENT = ['#D9CBB3', '#766858'];
const MAX_MESSAGE = 240;
const DEFAULT_POSITION = '50% 50%';

function parsePosition(pos) {
  const m = (pos || DEFAULT_POSITION).match(/(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!m) return [50, 50];
  return [parseFloat(m[1]), parseFloat(m[2])];
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function DraggablePhoto({ src, position, onPositionChange, size = 80 }) {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0 && e.pointerType !== 'touch') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const [px, py] = parsePosition(position);
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: px, posY: py };
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const scale = 100 / size;
    const nx = clamp(dragRef.current.posX - dx * scale, 0, 100);
    const ny = clamp(dragRef.current.posY - dy * scale, 0, 100);
    onPositionChange(`${Math.round(nx)}% ${Math.round(ny)}%`);
  };

  const onPointerUp = (e) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <div
      className="rr-photo-preview rr-photo-draggable"
      role="presentation"
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        overflow: 'hidden',
        position: 'relative',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      title={t('referrals.dragToReposition')}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: position || DEFAULT_POSITION,
          userSelect: 'none',
          pointerEvents: 'none',
          display: 'block',
        }}
      />
    </div>
  );
}

function publicBaseUrl() {
  if (typeof window === 'undefined') return 'https://app.dathent.ai';
  return window.location.origin;
}

function platformHsl(seed) {
  let h = 0;
  for (let i = 0; i < (seed || '').length; i++) {
    h = (h + seed.charCodeAt(i) * 37) % 360;
  }
  return `hsl(${h}, 30%, 70%)`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function ReferralCardPreview({ name, studio, message, photoUrl, photoPosition, gradient }) {
  const { t } = useTranslation();
  const grad = Array.isArray(gradient) && gradient.length >= 2 ? gradient : DEFAULT_GRADIENT;
  return (
    <div className="rr-preview-card">
      <div className="rr-preview-grad" />
      <div className="rr-preview-inner">
        <div className="rr-preview-eyebrow">{t('referrals.invitedBy')}</div>
        <div className="rr-preview-author">
          {photoUrl ? (
            <img
              className="rr-preview-avatar"
              src={photoUrl}
              alt=""
              style={{ objectPosition: photoPosition || '50% 50%' }}
            />
          ) : (
            <div
              className="rr-preview-avatar"
              style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
            >
              {(name || 'A').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="rr-preview-name">{name || t('referrals.yourName')}</div>
            <div className="rr-preview-studio">{studio || t('referrals.yourStudio')}</div>
          </div>
        </div>
        <p className="rr-preview-msg">
          "{message || t('referrals.inviteMessagePlaceholder')}"
        </p>
        <div className="rr-preview-perk">
          <I.sparkles size={11} />
          <span>{t('referrals.friendsGet')} <b>{t('referrals.twoMonthsFree')}</b></span>
        </div>
      </div>
    </div>
  );
}

function SetupWizard({ user, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || (user?.email ? user.email.split('@')[0] : ''));
  const [studio, setStudio] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError(t('referrals.displayNameRequired')); return; }
    setBusy(true);
    setError(null);
    try {
      const result = await api.referral.upsertMyProfile({
        displayName: name.trim(),
        studio: studio.trim() || null,
        customMessage: null,
      });
      onCreated(result);
    } catch (err) {
      setError(err?.message || t('referrals.failedToCreateProfile'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rr-setup card" onSubmit={submit}>
      <div>
        <h2>{t('referrals.startYourReferralProgram')}</h2>
        <p>{t('referrals.setupIntro')}</p>
      </div>
      <div className="rr-field">
        <label className="rr-l">{t('referrals.displayName')}</label>
        <input
          className="rr-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder={t('referrals.displayNamePlaceholder')}
          required
        />
      </div>
      <div className="rr-field">
        <label className="rr-l">{t('referrals.studioCompanyOptional')}</label>
        <input
          className="rr-input"
          value={studio}
          onChange={(e) => setStudio(e.target.value)}
          maxLength={120}
          placeholder={t('referrals.studioPlaceholder')}
        />
      </div>
      {error && <p className="rr-setup-error">{error}</p>}
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? t('referrals.creating') : t('referrals.createMyReferralLink')}
      </button>
    </form>
  );
}

/* ── Referral v2 — partner application popup (lean: rules + entity + tier) ──── */
function ApplyModal({ resubmit, reason, onClose, onApplied }) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [tier, setTier] = useState('regular');
  const [entity, setEntity] = useState('company');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { setError(t('referrals.v2.mustAccept', 'Please accept the program rules to continue.')); return; }
    setBusy(true); setError(null);
    try {
      const res = await api.referral.apply({
        acceptedRules: true,
        desiredTier: tier,
        legalEntityType: entity,
        contactValue: contact.trim() || null,
      });
      onApplied(res);
    } catch (err) {
      setError(err?.message || t('referrals.v2.applyFailed', 'Could not submit your application. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rr-modal-backdrop" onClick={onClose}>
      <form className="rr-apply card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="rr-apply-head">
          <h2>{resubmit ? t('referrals.v2.resubmitTitle', 'Update your application') : t('referrals.v2.applyTitle', 'Join the partner program')}</h2>
          <button type="button" className="rr-x" onClick={onClose} aria-label={t('referrals.v2.close', 'Close')}><I.x size={16} /></button>
        </div>
        <p className="rr-apply-sub">{resubmit
          ? t('referrals.v2.resubmitSub', 'Update your details below and resubmit — our team will take another look.')
          : t('referrals.v2.applySub', 'Earn lifetime commission for every client you bring to Dathent. Tell us a little about you — we review every application.')}</p>

        {resubmit && reason && (
          <div className="rr-resubmit-note">
            <span className="rr-l">{t('referrals.v2.reviewerAsked', 'What the reviewer asked for')}</span>
            <p>{reason}</p>
          </div>
        )}

        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.tier', 'Partner level')}</label>
          <div className="rr-seg">
            <button type="button" className={tier === 'regular' ? 'on' : ''} onClick={() => setTier('regular')}>{t('referrals.v2.tierRegular', 'Regular — 10%')}</button>
            <button type="button" className={tier === 'master' ? 'on' : ''} onClick={() => setTier('master')}>{t('referrals.v2.tierMaster', 'Master — 20% + team')}</button>
          </div>
          <p className="rr-hint">{t('referrals.v2.tierHint', 'Your final level is confirmed by our team on approval.')}</p>
        </div>

        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.entity', 'You invoice as')}</label>
          <div className="rr-seg">
            <button type="button" className={entity === 'company' ? 'on' : ''} onClick={() => setEntity('company')}>{t('referrals.v2.entityCompany', 'Company')}</button>
            <button type="button" className={entity === 'entrepreneur' ? 'on' : ''} onClick={() => setEntity('entrepreneur')}>{t('referrals.v2.entityEntrepreneur', 'Entrepreneur')}</button>
          </div>
        </div>

        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.contact', 'Payout contact (optional)')}</label>
          <input className="rr-input" value={contact} onChange={(e) => setContact(e.target.value)} maxLength={200} placeholder={t('referrals.v2.contactPlaceholder', 'Email, Telegram or phone')} />
        </div>

        <label className="rr-check">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>{t('referrals.v2.accept', 'I accept the partner program rules and payout terms.')}</span>
        </label>

        {error && <p className="rr-setup-error">{error}</p>}
        <button type="submit" className="btn btn-primary" disabled={busy || !accepted}>
          {busy ? t('referrals.v2.submitting', 'Submitting…') : (resubmit ? t('referrals.v2.resubmit', 'Resubmit application') : t('referrals.v2.submit', 'Submit application'))}
        </button>
      </form>
    </div>
  );
}

/* ── Referral v2 — S0-S5 lifecycle gate (active partners fall through to S5) ── */
function ReferralGate({ state, reason, onApplied }) {
  const { t } = useTranslation();
  const [showApply, setShowApply] = useState(false);

  const cfg = {
    s0: { Icon: I.flag, title: t('referrals.v2.s0Title', 'Partner program'), body: t('referrals.v2.s0Body', 'The partner program is open to active Dathent subscribers. Activate a plan to start earning lifetime commission for every client you refer.') },
    s1: { Icon: I.zap, title: t('referrals.v2.s1Title', 'Become a Dathent partner'), body: t('referrals.v2.s1Body', 'Refer clients and earn lifetime commission on every payment they make. Applying takes under a minute — we review each application.'), cta: t('referrals.v2.apply', 'Apply now') },
    s2: { Icon: I.clock, title: t('referrals.v2.s2Title', 'Application under review'), body: t('referrals.v2.s2Body', "Thanks for applying! Our team is reviewing your application — you'll get a notification as soon as it's approved.") },
    s3: { Icon: I.info, title: t('referrals.v2.s3Title', 'We need a bit more info'), body: reason || t('referrals.v2.s3Body', 'We need a few more details before we can approve your application. Please review and resubmit.'), cta: t('referrals.v2.updateResubmit', 'Update & resubmit') },
    s4: { Icon: I.alert, title: t('referrals.v2.s4Title', 'Application not approved'), body: reason || t('referrals.v2.s4Body', "Unfortunately we couldn't approve your application at this time."), cta: t('referrals.v2.reapply', 'Apply again') },
  }[state] || {};
  const { Icon } = cfg;

  return (
    <div className="rr-root">
      <div className="rr-gate card">
        {Icon && <div className={`rr-gate-icon rr-gate-${state}`}><Icon size={22} /></div>}
        <h2>{cfg.title}</h2>
        <p>{cfg.body}</p>
        <div className="rr-gate-actions">
          {cfg.cta && (
            <button className="btn btn-primary" onClick={() => setShowApply(true)}>{cfg.cta}</button>
          )}
          {state === 's0' && (
            <Link className="btn btn-primary" to="/app/plans">{t('referrals.v2.viewPlans', 'View plans')}</Link>
          )}
        </div>
      </div>
      {showApply && (
        <ApplyModal
          resubmit={state === 's3' || state === 's4'}
          reason={state === 's3' ? reason : null}
          onClose={() => setShowApply(false)}
          onApplied={(r) => { setShowApply(false); onApplied(r); }}
        />
      )}
    </div>
  );
}

/* ── Referral v2 — withdraw popup (requisites + invoice ref; manual payout) ─── */
function WithdrawModal({ availableCents, thresholdCents, onClose, onDone }) {
  const { t } = useTranslation();
  const maxEur = Math.floor((availableCents || 0) / 100);
  const minEur = Math.ceil((thresholdCents || 0) / 100);
  const [amount, setAmount] = useState(maxEur);
  const [bank, setBank] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const cents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) { setError(t('referrals.v2.invalidAmount', 'Enter a valid amount.')); return; }
    if (cents < (thresholdCents || 0)) { setError(t('referrals.v2.belowMin', 'Minimum withdrawal is €{{min}}.', { min: minEur })); return; }
    if (cents > (availableCents || 0)) { setError(t('referrals.v2.aboveMax', 'Amount exceeds your available balance.')); return; }
    if (!bank.trim()) { setError(t('referrals.v2.bankRequired', 'Add your bank details for the payout.')); return; }
    setBusy(true); setError(null);
    try {
      await api.referral.requestPayout({
        amountCents: cents,
        method: 'bank',
        methodDetails: { bank: bank.trim().slice(0, 500), invoiceRef: invoiceRef.trim().slice(0, 120) },
        notes: notes.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(err?.message || t('referrals.v2.withdrawFailed', 'Could not submit your request. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rr-modal-backdrop" onClick={onClose}>
      <form className="rr-apply card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="rr-apply-head">
          <h2>{t('referrals.v2.withdrawTitle', 'Withdraw earnings')}</h2>
          <button type="button" className="rr-x" onClick={onClose} aria-label={t('referrals.v2.close', 'Close')}><I.x size={16} /></button>
        </div>
        <p className="rr-apply-sub">{t('referrals.v2.withdrawSub', 'Request a payout of your available balance. We pay by bank transfer against your invoice — minimum €{{min}}.', { min: minEur })}</p>

        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.amount', 'Amount (€)')}</label>
          <input className="rr-input" type="number" min={minEur} max={maxEur} value={amount} onChange={(e) => setAmount(e.target.value)} />
          <p className="rr-hint">{t('referrals.v2.availableHint', 'Available: €{{amt}}', { amt: maxEur })}</p>
        </div>
        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.bank', 'Bank details / IBAN')}</label>
          <textarea className="rr-input" rows={2} value={bank} onChange={(e) => setBank(e.target.value)} maxLength={500} placeholder={t('referrals.v2.bankPlaceholder', 'Account holder, IBAN, bank name')} />
        </div>
        <div className="rr-field">
          <label className="rr-l">{t('referrals.v2.invoiceRef', 'Invoice reference (optional)')}</label>
          <input className="rr-input" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} maxLength={120} placeholder={t('referrals.v2.invoiceRefPlaceholder', 'Invoice no. you will send us')} />
        </div>

        {error && <p className="rr-setup-error">{error}</p>}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('referrals.v2.submitting', 'Submitting…') : t('referrals.v2.requestWithdrawal', 'Request withdrawal')}
        </button>
      </form>
    </div>
  );
}

export function Referrals() {
  const { user } = useAuth?.() || {};
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ signups: 0, activated: 0, pendingPayoutCents: 0, earnedCents: 0, paidOutCents: 0 });
  const [signups, setSignups] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [payoutBusy, setPayoutBusy] = useState(false);

  // Referral v2 lifecycle (S0-S5). Defaults keep the legacy path intact: when
  // the GET omits these fields (flag off), hasActiveSub stays true and appStatus
  // resolves to 'active', so the v2 gate never intercepts.
  const v2 = referralProgramV2Enabled();
  const [hasActiveSub, setHasActiveSub] = useState(true);
  const [appStatus, setAppStatus] = useState('active');
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const fileRef = useRef(null);
  const debounceRef = useRef(null);

  const [draftName, setDraftName] = useState('');
  const [draftStudio, setDraftStudio] = useState('');
  const [draftMessage, setDraftMessage] = useState('');
  const [draftPosition, setDraftPosition] = useState(DEFAULT_POSITION);
  const positionDebounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const me = await api.referral.getMyProfile();
        if (cancelled) return;
        setHasActiveSub(me?.hasActiveSubscription !== false);
        setAppStatus(me?.applicationStatus || (me?.profile ? 'active' : 'none'));
        if (me?.profile) {
          setProfile(me.profile);
          if (me.stats) setStats({ ...stats, ...me.stats });
          setDraftName(me.profile.displayName || '');
          setDraftStudio(me.profile.studio || '');
          setDraftMessage(me.profile.customMessage || '');
          setDraftPosition(me.profile.photoPosition || DEFAULT_POSITION);
          const [sg, py] = await Promise.all([
            api.referral.listSignups().catch(() => ({ signups: [], counts: { total: 0, activated: 0 } })),
            api.referral.listPayouts().catch(() => ({ payouts: [] })),
          ]);
          if (cancelled) return;
          setSignups(sg.signups || []);
          setPayouts(py.payouts || []);
          if (sg.counts) {
            setStats((prev) => ({ ...prev, signups: sg.counts.total, activated: sg.counts.activated }));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const link = profile ? `${publicBaseUrl()}/r/${profile.code}` : '';

  const copyLink = useCallback(() => {
    if (!link) return;
    navigator.clipboard?.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [link]);

  const debouncedSave = useCallback((patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSavingField('field');
      try {
        const result = await api.referral.upsertMyProfile(patch);
        if (result?.profile) setProfile(result.profile);
      } catch (err) {
        console.error('Failed to save referrer profile', err);
      } finally {
        setSavingField(null);
      }
    }, 600);
  }, []);

  const onNameChange = (v) => {
    setDraftName(v);
    debouncedSave({ displayName: v, regenerateCode: true });
  };
  const onStudioChange = (v) => {
    setDraftStudio(v);
    debouncedSave({ studio: v, regenerateCode: true });
  };
  const onMessageChange = (v) => {
    const next = v.slice(0, MAX_MESSAGE);
    setDraftMessage(next);
    debouncedSave({ customMessage: next });
  };
  const onPositionChange = (next) => {
    setDraftPosition(next);
    if (positionDebounceRef.current) clearTimeout(positionDebounceRef.current);
    positionDebounceRef.current = setTimeout(async () => {
      try {
        const result = await api.referral.upsertMyProfile({ photoPosition: next });
        if (result?.profile) setProfile(result.profile);
      } catch (err) {
        console.error('Failed to save photo position', err);
      }
    }, 400);
  };

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(t('referrals.imageMustBeUnder5Mb'));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError(t('referrals.onlyImageFormatsSupported'));
      return;
    }
    try {
      const result = await api.referral.uploadPhoto(file);
      if (result?.photoUrl) {
        setProfile((p) => (p ? { ...p, photoUrl: result.photoUrl } : p));
      }
    } catch (err) {
      setUploadError(err?.message || t('referrals.uploadFailed'));
    }
  };

  const removePhoto = async () => {
    try {
      const result = await api.referral.upsertMyProfile({ photoUrl: null });
      if (result?.profile) setProfile(result.profile);
    } catch (err) {
      setUploadError(err?.message || t('referrals.failedToRemovePhoto'));
    }
  };

  const filteredSignups = useMemo(() => {
    if (filter === 'all') return signups;
    return signups.filter((s) => s.status === filter);
  }, [signups, filter]);

  const requestPayout = async () => {
    if (stats.pendingPayoutCents <= 0 || payoutBusy) return;
    setPayoutBusy(true);
    try {
      await api.referral.requestPayout({ amountCents: stats.pendingPayoutCents });
      const py = await api.referral.listPayouts();
      setPayouts(py.payouts || []);
    } catch (err) {
      console.error('Payout request failed', err);
    } finally {
      setPayoutBusy(false);
    }
  };

  // v2 withdraw: re-pull balances + payouts after a request so available drops
  // and the new pending payout shows immediately.
  const refreshAfterPayout = useCallback(async () => {
    const [me, py] = await Promise.all([
      api.referral.getMyProfile().catch(() => null),
      api.referral.listPayouts().catch(() => ({ payouts: [] })),
    ]);
    if (me?.stats) setStats((prev) => ({ ...prev, ...me.stats }));
    setPayouts(py.payouts || []);
  }, []);

  if (loading) {
    return (
      <div className="rr-root">
        <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          {t('referrals.loading')}
        </div>
      </div>
    );
  }

  // Referral v2 — S0-S5 lifecycle. Active partners (S5) fall through to the
  // dashboard below; everyone else gets the gated state. Off => legacy path.
  if (v2 && appStatus !== 'active') {
    const apply = (r) => {
      if (r?.profile) {
        setProfile(r.profile);
        setAppStatus(r.profile.applicationStatus || 'review');
        setDraftName(r.profile.displayName || '');
        setDraftStudio(r.profile.studio || '');
      } else {
        setAppStatus('review');
      }
    };
    if (!hasActiveSub) return <ReferralGate state="s0" />;
    if (appStatus === 'review') return <ReferralGate state="s2" />;
    if (appStatus === 'needs_data') return <ReferralGate state="s3" reason={profile?.rejectionReason} onApplied={apply} />;
    if (appStatus === 'rejected') return <ReferralGate state="s4" reason={profile?.rejectionReason} onApplied={apply} />;
    return <ReferralGate state="s1" onApplied={apply} />;
  }

  if (!profile) {
    return (
      <div className="rr-root">
        <SetupWizard user={user} onCreated={(r) => {
          if (r?.profile) {
            setProfile(r.profile);
            setDraftName(r.profile.displayName || '');
            setDraftStudio(r.profile.studio || '');
            setDraftMessage(r.profile.customMessage || '');
          }
        }} />
      </div>
    );
  }

  const gradient = profile.avatarGradient || DEFAULT_GRADIENT;
  // v2 rate is the partner's tier (regular 10 / master 20), commission is
  // LIFETIME — not the legacy 30% / "first year".
  const rateDisplay = v2 ? (profile.partnerType === 'master' ? 20 : 10) : profile.referralRate;

  return (
    <div className="rr-root">
      <div className="rr-top">
        <div>
          <Link className="rr-back" to="/app/settings"><I.chevLeft size={14} /> {t('referrals.settings')}</Link>
          <h1 className="rr-h">{t('referrals.referralProgram')}</h1>
          <p className="rr-sub">
            {v2
              ? t('referrals.v2.intro', 'Refer clients and earn {{rate}}% lifetime commission on every payment they make. They get 2 months free.', { rate: rateDisplay })
              : t('referrals.referralIntro', { rate: profile.referralRate })}
          </p>
        </div>
        {v2 ? (
          <div className="rr-withdraw-cta">
            <button
              className="btn btn-primary"
              onClick={() => setWithdrawOpen(true)}
              disabled={(stats.availableCents || 0) < (stats.payoutThresholdCents || 0) || payoutBusy}
            >
              <I.zap size={13} /> {t('referrals.v2.withdrawAmount', 'Withdraw €{{amount}}', { amount: ((stats.availableCents || 0) / 100).toFixed(0) })}
            </button>
            {(stats.availableCents || 0) < (stats.payoutThresholdCents || 0) && (
              <span className="rr-withdraw-hint">
                {t('referrals.v2.needMore', '€{{amt}} more to withdraw', { amt: Math.max(0, ((stats.payoutThresholdCents || 0) - (stats.availableCents || 0)) / 100).toFixed(0) })}
              </span>
            )}
          </div>
        ) : (
          <button
            className="btn btn-primary"
            onClick={requestPayout}
            disabled={stats.pendingPayoutCents <= 0 || payoutBusy}
          >
            <I.zap size={13} /> {t('referrals.requestPayoutAmount', { amount: (stats.pendingPayoutCents / 100).toFixed(0) })}
          </button>
        )}
      </div>

      {v2 && withdrawOpen && (
        <WithdrawModal
          availableCents={stats.availableCents || 0}
          thresholdCents={stats.payoutThresholdCents || 0}
          onClose={() => setWithdrawOpen(false)}
          onDone={() => { setWithdrawOpen(false); refreshAfterPayout(); }}
        />
      )}

      <div className="rr-stats">
        <div className="card rr-stat">
          <div className="rr-stat-l">{t('referrals.totalEarned')}</div>
          <div className="rr-stat-v">€{(stats.earnedCents / 100).toLocaleString()}</div>
          <div className="rr-stat-sub">{t('referrals.allTimeGross')}</div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">{v2 ? t('referrals.v2.available', 'Available') : t('referrals.pendingPayout')}</div>
          <div className="rr-stat-v">€{(((v2 ? stats.availableCents : stats.pendingPayoutCents) || 0) / 100).toFixed(0)}</div>
          <div className="rr-stat-sub">
            {v2
              ? ((stats.heldCents || 0) > 0
                  ? t('referrals.v2.plusHeld', '+€{{amt}} in hold', { amt: ((stats.heldCents || 0) / 100).toFixed(0) })
                  : ((stats.availableCents || 0) > 0 ? t('referrals.readyToRequest') : t('referrals.noBalanceYet')))
              : (stats.pendingPayoutCents > 0 ? t('referrals.readyToRequest') : t('referrals.noBalanceYet'))}
          </div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">{t('referrals.signups')}</div>
          <div className="rr-stat-v">{stats.signups}</div>
          <div className="rr-stat-sub">{t('referrals.nActivated', { count: stats.activated })}</div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">{t('referrals.activeReferrals')}</div>
          <div className="rr-stat-v">{stats.activated}</div>
          <div className="rr-stat-sub">
            {v2
              ? t('referrals.v2.earningLifetime', 'earning {{rate}}% lifetime', { rate: rateDisplay })
              : t('referrals.earningRecurring', { rate: profile.referralRate })}
          </div>
        </div>
      </div>

      <div className="rr-grid">
        <div className="rr-col">
          <section className="card rr-card">
            <header className="rr-card-h">
              <div className="eyebrow"><span>{t('referrals.yourLink')}</span></div>
              <h2>{t('referrals.shareThisAnywhere')}</h2>
              <p>{t('referrals.shareThisAnywhereDesc')}</p>
            </header>
            <div className="rr-link">
              <span className="rr-link-prefix">{publicBaseUrl().replace(/^https?:\/\//, '')}/r/</span>
              <span className="rr-link-code">{profile.code}</span>
              <button type="button" className={`rr-copy ${copied ? 'is-copied' : ''}`} onClick={copyLink}>
                {copied ? (<><I.check size={13} /> {t('referrals.copied')}</>) : (<><I.copy size={13} /> {t('referrals.copy')}</>)}
              </button>
            </div>
            <div className="rr-share">
              <span className="rr-share-l">{t('referrals.quickShare')}</span>
              <a className="rr-share-btn" href={`mailto:?subject=${encodeURIComponent('Try Dathent')}&body=${encodeURIComponent(link)}`}>
                <I.send size={12} /> {t('referrals.email')}
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`https://t.me/share/url?url=${encodeURIComponent(link)}`}>
                <I.send size={12} /> Telegram
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`}>
                <I.link size={12} /> LinkedIn
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`/r/${profile.code}`}>
                <I.link size={12} /> {t('referrals.open')}
              </a>
            </div>
          </section>

          <section className="card rr-card">
            <header className="rr-card-h">
              <div className="eyebrow"><span>{t('referrals.personalizeTheInvite')}</span></div>
              <h2>{t('referrals.howFriendsSeeYourInvite')}</h2>
              <p>{t('referrals.howFriendsSeeYourInviteDesc')}</p>
            </header>
            <div className="rr-form">
              <div className="rr-field">
                <label className="rr-l">{t('referrals.profilePhoto')}</label>
                <div className="rr-photo">
                  {profile.photoUrl ? (
                    <DraggablePhoto
                      src={profile.photoUrl}
                      position={draftPosition}
                      onPositionChange={onPositionChange}
                      size={80}
                    />
                  ) : (
                    <div
                      className="rr-photo-preview"
                      style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}
                    >
                      {(draftName || 'A').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="rr-photo-actions">
                    <div>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>
                        <I.upload size={12} /> {profile.photoUrl ? t('referrals.replace') : t('referrals.uploadPhoto')}
                      </button>
                      {profile.photoUrl && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={removePhoto}>
                          <I.trash size={12} /> {t('referrals.remove')}
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={onPhotoPick}
                    />
                    <div className="rr-photo-hint">
                      {t('referrals.photoHint')} {profile.photoUrl ? t('referrals.dragPhotoToReposition') : ''}
                    </div>
                    {uploadError && <div className="rr-photo-error">{uploadError}</div>}
                  </div>
                </div>
              </div>

              <div className="rr-field">
                <label className="rr-l">{t('referrals.displayName')}</label>
                <input
                  className="rr-input"
                  value={draftName}
                  onChange={(e) => onNameChange(e.target.value)}
                  maxLength={80}
                />
              </div>

              <div className="rr-field">
                <label className="rr-l">{t('referrals.studioCompany')}</label>
                <input
                  className="rr-input"
                  value={draftStudio}
                  onChange={(e) => onStudioChange(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="rr-field">
                <div className="rr-l-row">
                  <label className="rr-l">{t('referrals.inviteMessage')}</label>
                  <span className="rr-l-count">{draftMessage.length} / {MAX_MESSAGE}</span>
                </div>
                <textarea
                  className="rr-input rr-textarea"
                  rows={3}
                  maxLength={MAX_MESSAGE}
                  value={draftMessage}
                  onChange={(e) => onMessageChange(e.target.value)}
                />
              </div>
              {savingField && (
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{t('referrals.saving')}</div>
              )}
            </div>
          </section>

          <section className="card rr-card">
            <header className="rr-card-h rr-card-h-row">
              <div>
                <div className="eyebrow"><span>{t('referrals.referrals')}</span></div>
                <h2>{t('referrals.peopleWhoSignedUp')}</h2>
              </div>
              <div className="rr-tabs">
                {[
                  { id: 'all', label: t('referrals.tabAll') },
                  { id: 'waitlist', label: t('referrals.tabWaitlist') },
                  { id: 'activated', label: t('referrals.tabActivated') },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`rr-tab ${filter === tab.id ? 'is-active' : ''}`}
                    onClick={() => setFilter(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </header>

            <div className="rr-table">
              <div className="rr-table-h">
                <span>{t('referrals.person')}</span>
                <span>{t('referrals.status')}</span>
                <span>{t('referrals.joined')}</span>
                <span style={{ textAlign: 'right' }}>{t('referrals.contact')}</span>
              </div>
              {filteredSignups.length === 0 && (
                <div className="rr-table-empty">{t('referrals.noSignupsYet')}</div>
              )}
              {filteredSignups.map((r) => (
                <div key={r.id} className="rr-table-row">
                  <div className="rr-person">
                    <div className="rr-person-av" style={{ background: platformHsl(r.email) }}>
                      {(r.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="rr-person-name">{r.email}</div>
                      <div className="rr-person-sub">{r.locale?.toUpperCase() || 'EN'}</div>
                    </div>
                  </div>
                  <span className={`pill rr-pill-${r.status}`}>
                    <span className="dot" />
                    {r.status === 'activated' ? t('referrals.statusActivated') : r.status === 'churned' ? t('referrals.statusChurned') : t('referrals.statusWaitlist')}
                  </span>
                  <span className="rr-cell-meta">{formatDate(r.createdAt)}</span>
                  <span className="rr-cell-contact">
                    {r.contactValue || '—'}
                    {r.contactKind && r.contactValue && <span>{r.contactKind}</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="rr-side">
          <div className="rr-side-sticky">
            <div className="rr-side-h">
              <div className="eyebrow"><span>{t('referrals.livePreview')}</span></div>
              <h3>{t('referrals.yourSignupPage')}</h3>
              <p>
                {t('referrals.updatesAsYouEdit')}{' '}
                <a href={`/r/${profile.code}`} target="_blank" rel="noreferrer">{t('referrals.openFullPreview')}</a>
              </p>
            </div>
            <ReferralCardPreview
              name={draftName}
              studio={draftStudio}
              message={draftMessage}
              photoUrl={profile.photoUrl}
              photoPosition={draftPosition}
              gradient={gradient}
            />

            <section className="card rr-payouts">
              <header className="rr-card-h">
                <div className="eyebrow"><span>{t('referrals.payouts')}</span></div>
                <h3>{t('referrals.recent')}</h3>
              </header>
              <div className="rr-payout-list">
                {payouts.length === 0 && (
                  <div className="rr-payouts-empty">{t('referrals.noPayoutsYet')}</div>
                )}
                {payouts.map((p) => (
                  <div key={p.id} className="rr-payout">
                    <div className="rr-payout-l">
                      <div className="rr-payout-amount">€{(p.amountCents / 100).toFixed(0)}</div>
                      <div className="rr-payout-method">{p.method || t('referrals.pendingMethod')}</div>
                    </div>
                    <div className="rr-payout-r">
                      <span className="rr-payout-when">{formatDate(p.requestedAt)}</span>
                      <span className={`pill ${p.status === 'paid' ? 'pill-success' : ''}`} style={{ height: 18 }}>
                        {p.status === 'paid' ? <I.check size={9} /> : null}
                        {' '}{p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Referrals;
